import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Client from 'fhirclient/lib/Client';
import { Actions, useAdministrationStateContext } from '../components/state';
import { ComboBox, CommandBar, IComboBox, IComboBoxOption, ICommandBarItemProps, ICommandBarStyles, IListProps, ITextFieldStyles, MessageBar, MessageBarType, Panel, PanelType, SelectionMode, Separator, ShimmeredDetailsList, Stack, TextField } from 'office-ui-fabric-react';
import { useBoolean } from '@uifabric/react-hooks';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { MedicationsData } from './medicationData'
import { DoseUnitData } from './doseUnitData'
import { RouteData } from './routeData'
import { MedicationColumns } from './medicationColumns'
import { fhirclient } from 'fhirclient/lib/types';

const Content = styled.div`
    margin: 1rem;
    height: 75vh;
`

const Spacing = styled.div`
    margin: 1rem;
`
const commandBarStyles: Partial<ICommandBarStyles> = { root: { padding: '0px' } };
const comboBoxStyle = { maxWidth: 300 };
const unitComboBoxStyle = { maxWidth: 80 };
const amountFieldStyles: Partial<ITextFieldStyles> = { fieldGroup: { width: 200, marginRight: '1rem' } };
const textFieldStyles: Partial<ITextFieldStyles> = { fieldGroup: { width: 400 } };

export type MedicationListItem = {
    id: string;
    dosageInstruction: string;
    dosageAmount: number;
    dosageUnit: string;
    dosageRoute: string;
    medicationText: string;
    medicationId: string;
    status: string;
    }

export type MedicationsProps = {
    client: Client;
    patient: fhirclient.FHIR.Patient;
  }

export function Medications({ client, patient }: MedicationsProps){
    const { dispatch } = useAdministrationStateContext();
    const [displayShimmer, setDisplayShimmer] = useState(true);
    const [type, setType] = useState('');
    const [message, setMessage] = useState('');
    const [medications, setMedications] = useState<any[]>([]);
    const [medicationsList, setMedicationsList] = useState<(MedicationListItem | null)[] | undefined>(undefined);

    const [selectedMedication, setSelectedMedication] = useState<any>({});
    const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);

    const [medicationsSelection, setMedicationsSelection] = React.useState([] as IComboBoxOption[]);
    const [selectedMedicationId, setSelectedMedicationId] = React.useState<string | undefined>('');

    const [doseText, setDoseText] = React.useState('');

    const [doseUnitSelection, setDoseUnitSelection] = React.useState([] as IComboBoxOption[]);
    const [doseRouteSelection, setDoseRouteSelection] = React.useState([] as IComboBoxOption[]);
    const [selectedDoseUnitId, setSelectedDoseUnitId] = React.useState<string | undefined>('');
    const [selectedDoseRouteId, setSelectedDoseRouteId] = React.useState<string | undefined>('');

    const [doseAmount, setDoseAmount] = React.useState(1);

    const loadMedicationsData = useCallback(() => {
        Promise.all([
            client.request(`/MedicationRequest?patient=${client.patient.id}`, {
                resolveReferences: "medicationReference",
                pageLimit: 0,
                flat: true
            }),
            ])
            .then(
            ([meds]) => {
                setMedications(meds);
            },
            error => {
                console.error(error);
            }
            ).catch((error) => {
                dispatch(Actions.AddError({message: 'Error occured while fetching patient details.', stacktrace: error.message}))
            }
            )
    }, [client, dispatch]);

    useEffect(() => {
        const medicationsSelectionData = MedicationsData.map((med: any) => {
            return { key: med.id, text: med.text }
        });
        setMedicationsSelection(medicationsSelectionData);

        const doseUnitSelectionData = DoseUnitData.map((dose: any) => {
            return { key: dose.id, text: dose.text }
        });
        setDoseUnitSelection(doseUnitSelectionData);

        const doseRouteSelectionData = RouteData.map((route: any) => {
            return { key: route.id, text: route.text }
        });
        setDoseRouteSelection(doseRouteSelectionData);
    }, []);

    useEffect(() => {
        loadMedicationsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client, dispatch]);

    useEffect(() => {
        if(medications) {
            const ListMedications = medications.map((med: any) => {
                let dosageInstruction = {text: 'N/A', route: {text: 'N/A', coding: [{code: ''}]}, doseAndRate: [{doseQuantity: { value: '', unit: '', system: '', code: ''}}]};
                let medication = {text: 'N/A', coding: [{code: ''}]};
                let route = {text: 'N/A', coding: [{code: ''}]};
                
                if(med.medicationCodeableConcept)
                {
                    medication = med.medicationCodeableConcept;
                }

                if(med.dosageInstruction)
                {
                    if(med.dosageInstruction.length > 0)
                    {
                        dosageInstruction = med.dosageInstruction[0];

                        if(dosageInstruction.route)
                        {
                            route = dosageInstruction.route;
                        }
                    }
                }

                return {
                    id: med.id, 
                    dosageInstruction: dosageInstruction.text, 
                    dosageAmount: dosageInstruction.doseAndRate ? Number(dosageInstruction.doseAndRate[0].doseQuantity.value) : 0,
                    dosageUnit: dosageInstruction.doseAndRate ? dosageInstruction.doseAndRate[0].doseQuantity.unit : '',
                    dosageRoute: route.text,
                    medicationText: medication.text, 
                    medicationId: medication.coding[0].code, 
                    status: med.status
                } as MedicationListItem
            });

            setMedicationsList(ListMedications);
            setDisplayShimmer(false);
        }
    }, [medications]);

    const shimmeredDetailsListProps: IListProps = {
        renderedWindowsAhead: 0,
        renderedWindowsBehind: 0,
    };

    function downloadJson() {
        if(selectedMedication) {
            const blob = new Blob([ JSON.stringify(selectedMedication, null, 2) ], { type: 'application/json' });
            const blobHref = window.URL.createObjectURL(blob)
            const fileName = selectedMedication.id || 'medication.json'

            const a = document.createElement("a")
            a.href = blobHref
            a.setAttribute("download", fileName)
            a.click()
        }
    };

    function save() {
        const medication = selectedMedication;

        if(medication !== undefined)
        {
            const newMedication = MedicationsData.find(med => med.id === selectedMedicationId);
            const newDoseUnit = DoseUnitData.find(dose => dose.id === selectedDoseUnitId);
            const newDoseRouteUnit = RouteData.find(route => route.id === selectedDoseRouteId);

            if(newMedication && newDoseUnit && newDoseRouteUnit)
            {
                if(!medication.medicationCodeableConcept)
                {
                    medication.medicationCodeableConcept = {
                    coding: [
                        {
                          system: "",
                          code: "",
                          display: "",
                          userSelected: false
                        }
                      ],
                      text: ""
                    }
                }
    
                if(!medication.medicationCodeableConcept.coding)
                {
                    medication.medicationCodeableConcept.coding = [
                        {
                            system: "",
                            code: "",
                            display: "",
                            userSelected: false
                        }
                    ]
                }
    
                medication.medicationCodeableConcept.coding[0].code = newMedication.id;
                medication.medicationCodeableConcept.text = newMedication.text;
                medication.medicationCodeableConcept.coding[0].system = newMedication.system;
                medication.medicationCodeableConcept.coding[0].display = newMedication.text;
                medication.medicationCodeableConcept.coding[0].userSelected = true;
    
                if(!medication.dosageInstruction)
                {
                    medication.dosageInstruction = [
                        {
                            text: "",
                            route: {
                                coding: [
                                    {
                                      system: "",
                                      code: "",
                                      display: "",
                                      userSelected: false
                                    }
                                  ],
                                text: ""
                            },
                            timing: {
                                repeat: {
                                  boundsPeriod: {
                                    start: "2008-08-13T00:00:00-06:00"
                                  },
                                  count: 0,
                                  frequency: 1,
                                  frequencyMax: 0,
                                  period: 1,
                                  periodUnit: "d"
                                }
                              },
                            doseAndRate: [
                                {
                                    doseQuantity: { 
                                        value: '',
                                        unit: '',
                                        system: '',
                                        code: ''
                                    }
                                }
                            ]
                        }];
                }

                if(!medication.dosageInstruction[0].route)
                {
                    medication.dosageInstruction[0].route = {
                        coding: [
                            {
                              system: "",
                              code: "",
                              display: "",
                              userSelected: false
                            }
                          ],
                        text: ""
                    };
                }
    
                medication.dosageInstruction[0].text = doseText;
                medication.dosageInstruction[0].doseAndRate[0].doseQuantity.value = doseAmount;
                medication.dosageInstruction[0].doseAndRate[0].doseQuantity.unit = newDoseUnit.id;
                medication.dosageInstruction[0].doseAndRate[0].doseQuantity.system = newDoseUnit.system;
                medication.dosageInstruction[0].doseAndRate[0].doseQuantity.code = newDoseUnit.code;

                medication.dosageInstruction[0].route.coding[0].code = newDoseRouteUnit.id;
                medication.dosageInstruction[0].route.coding[0].system = newDoseRouteUnit.system;
                medication.dosageInstruction[0].route.coding[0].display = newDoseRouteUnit.text;
                medication.dosageInstruction[0].route.coding[0].userSelected = true;
                medication.dosageInstruction[0].route.text = newDoseRouteUnit.text;

                let output = 'Successfully saved medication.';
                let outputType = 'success';
    
                if(medication.id)
                {
                    client.update(medication)
                    .catch((error) => {
                        output = `Error occured while updating the medication request. stacktrace: ${error.message}`;
                        outputType ='error';
                    }).then(() => {refresh();});
                }
                else{
                    client.create(medication)
                    .catch((error) => {
                        output = `Error occured while updating the medication request. stacktrace: ${error.message}`;
                        outputType ='error';
                    }).then(() => {refresh();});
                }
               
    
                setMessage(output);
                setType(outputType);
            }
        }
    };

    async function deleteMedication() {
        await client.delete(`MedicationRequest/${selectedMedication.id}`);
        dismissPanel();
        refresh();
    }

    async function addMedication() {
        setSelectedMedication({
            resourceType: "MedicationRequest",
            meta: {
                versionId: "10",
                lastUpdated: "2021-03-12T09:57:57.000+00:00",
                source: "#GwsDiJtjjM4i88cs"
            },
            status: "active",
            subject: {
                "reference": `Patient/${patient.id}`
            },
            authoredOn: "2001-01-01"
        });
        openPanel();
    }

    function refresh() {
        setDisplayShimmer(true);
        loadMedicationsData();
    }

    const mainMenu: ICommandBarItemProps[] = [
        {
            key: 'add',
            text: 'Add',
            iconProps: { iconName: 'Add' },
            onClick: () => addMedication(),
        },
        {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            onClick: () => refresh(),
        },
      ];

    const _items: ICommandBarItemProps[] = [
        {
          key: 'save',
          text: 'Save',
          iconProps: { iconName: 'Save' },
          onClick: () => save(),
        },
        {
          key: 'download',
          text: 'Download',
          iconProps: { iconName: 'Download' },
          onClick: () => downloadJson(),
        },
        {
            key: 'delete',
            text: 'Delete',
            iconProps: { iconName: 'Delete' },
            onClick: () => deleteMedication(),
          },
      ];

    const closePanel = () => {
        setSelectedMedication(undefined);
        setSelectedDoseRouteId(undefined);
        setType('');
        dismissPanel();
    };

    const selectItem = (item: MedicationListItem, index?: number) => {
        if(index !== undefined)
        {
            const med = medications[index];
            setSelectedMedication(med);

            if(med.dosageInstruction[0].route)
            {
                setSelectedDoseRouteId(med.dosageInstruction[0].route.coding[0].code);
            }

            setSelectedMedicationId(item.medicationId);
            setSelectedDoseUnitId(item.dosageUnit);
            setDoseAmount(item.dosageAmount);
            setDoseText(item.dosageInstruction);
            openPanel();
        }
    };

    const onDoseTextChange = React.useCallback(
        (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            if(newValue || newValue === '') 
            {
                setDoseText(newValue);
            }
        },
        [],
      );

    const onDoseAmountChange = React.useCallback(
        (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            if(newValue || Number(newValue) === 0) 
            {
                setDoseAmount(Number(newValue));
            }
        },
        [],
      );

    const onMedicationChange = React.useCallback(
    (ev: React.FormEvent<IComboBox>, option?: IComboBoxOption): void => {
        setSelectedMedicationId(option?.key.toString());
    },
    [setSelectedMedicationId],
    );

    const onDoseUnitChange = React.useCallback(
    (ev: React.FormEvent<IComboBox>, option?: IComboBoxOption): void => {
        setSelectedDoseUnitId(option?.key.toString());
    },
    [setSelectedDoseUnitId],
    );

    const onRouteChange = useCallback((ev: React.FormEvent<IComboBox>, option?: IComboBoxOption): void => {
        setSelectedDoseRouteId(option?.key.toString());
    },[setSelectedDoseRouteId]);

    return (
        <Content>
            <CommandBar
                items={mainMenu}
                styles={commandBarStyles}
                ariaLabel="Use left and right arrow keys to navigate between commands"
            />
            <ShimmeredDetailsList
                setKey="items"
                items={medicationsList || []}
                columns={MedicationColumns}
                onActiveItemChanged={selectItem}
                selectionMode={SelectionMode.none}
                enableShimmer={displayShimmer}
                ariaLabelForShimmer="Content is being fetched"
                ariaLabelForGrid="Item details"
                listProps={shimmeredDetailsListProps}
            />
            <Panel
                headerText="Edit Medication"
                isOpen={isOpen}
                type={PanelType.medium}
                onDismiss={closePanel}
                // You MUST provide this prop! Otherwise screen readers will just say "button" with no label.
                closeButtonAriaLabel="Close"
            >
                {isOpen &&(
                    <Stack>
                        <CommandBar
                            items={_items}
                            styles={commandBarStyles}
                            ariaLabel="Use left and right arrow keys to navigate between commands"
                        />
                        <Spacing>
                        </Spacing>
                        <Stack>
                            <Text variant={'large'}>Medication:</Text>
                            <ComboBox
                                style={comboBoxStyle}
                                selectedKey={selectedMedicationId}
                                label="Medication"
                                allowFreeform
                                autoComplete="on"
                                options={medicationsSelection}
                                onChange={onMedicationChange}
                                />
                            {/* <TextField label="Text" value={medicationText} onChange={updateMedicationText} /> */}
                        </Stack>
                        <Separator>
                        </Separator>
                        <Stack>
                            <Text variant={'large'}>Dosage:</Text>
                            <TextField
                                label="Dose Text"
                                value={doseText}
                                onChange={onDoseTextChange}
                                styles={textFieldStyles}
                            />
                            <Stack horizontal>
                            <TextField
                                label="Amount"
                                value={doseAmount.toString()}
                                onChange={onDoseAmountChange}
                                styles={amountFieldStyles}
                            />
                            <ComboBox
                                style={unitComboBoxStyle}
                                selectedKey={selectedDoseUnitId}
                                label="Unit"
                                allowFreeform
                                autoComplete="on"
                                options={doseUnitSelection}
                                onChange={onDoseUnitChange}
                                />
                            </Stack>
                            <ComboBox
                                style={comboBoxStyle}
                                selectedKey={selectedDoseRouteId}
                                label="Route"
                                allowFreeform
                                autoComplete="on"
                                options={doseRouteSelection}
                                onChange={onRouteChange}
                                />
                        </Stack>
                        <Spacing>
                        </Spacing>
                        <Stack>
                            {(type === 'error') && <MessageBar messageBarType={MessageBarType.error} isMultiline={true}>{message}</MessageBar>}
                            {(type === 'success') && <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>{message}</MessageBar>}
                        </Stack>
                    </Stack>
                )}
            </Panel>
        </Content>
    );
}
